import { getRequest } from "../util/util.js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// State Variables
let currSelection = 0;
let selectedBoats = [];
let selectedSeries = [];
let selectedBoxes = [];
let allBoatsData = [];
let allSeriesData = [];

// DOM Cache Elements
const boatSelectElems = document.querySelectorAll(".js--compare-boat__selection > [data-boat-id][data-boat-series]");
const boatFamilySelect = document.getElementById("select-boat-family");
const boatModelSelect = document.getElementById("select-boat-model");
const submitBtn = document.getElementById("boat-select-submitBtn");
const compareCheckbox = document.getElementById("compare-difference");
const compTab = document.getElementById("compare-tab");
const boatSelectModal = document.getElementById("boat-select-modal");
const compareAccordion = document.getElementById("compare-accordion");

// Configuration & API Endpoints
const ALL_BOATS_URL = `${location.origin}/umbraco/api/boats/GetAllBoatsWithoutTrimPackages`;
const ALL_SERIES_URL = `${location.origin}/umbraco/api/boats/GetAllSeries`;
const getBoatByIdUrl = (id) => `${location.origin}/umbraco/api/boats/GetBoatById/${id}`;

// Pure Utility UI Helpers
function cleanStringForId(str) {
  return str
    .trim()
    .replace(/[\s~`!@#$%^&*(){}\[\];:"'<,.>?\/\\|_+=-]/g, "")
    .toLowerCase();
}

// Map filter to original exact comparison semantics
function filterBoatsBySeriesName(seriesName, boatsList) {
  return boatsList.filter((boat) => {
    return boat.series.trim().toLowerCase() === seriesName.toLowerCase();
  });
}

function filterExcludedCollection(itemsToExclude, baseCollection, propertyName) {
  return baseCollection.filter((item) => {
    return !itemsToExclude.includes(item[propertyName]);
  });
}

// HTML Template Generation
function generateBoatHtml(boat) {
  const fallbackImg = boat.navigationImageUrl ? boat.navigationImageUrl : "";

  return `
        <div class="flex-col justify-start items-start gap-2 inline-flex">
            <div class="relative">
                <img class="w-11/12 object-fill h-[75px] md:h-[120px] lg:h-[134px]" src="${boat?.navigationImageUrl ?? ""}" />
                <div class="w-11/12 h-4 relative -z-10">
                    <div class="h-1 w-11/12 mx-auto opacity-30 bg-neutral-800 rounded-[100%] blur-sm "></div>
                </div>
            </div>
            <div class="self-stretch flex-col justify-start items-start gap-2 flex">
                <div class="justify-start items-center gap-2 inline-flex">
                    <h4 class="heading-4">${boat.name}</h4>
                    <div data-index="${currSelection}" onclick="lib.showModal(event)" class="js--inline-modal-trigger w-6 h-6 p-1 bg-[rgb(224,223,220)] rounded-full justify-center items-center flex cursor-pointer">
                        <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g id="Edit">
                                <path id="stylus" d="M0.803414 12.3937C0.570151 12.4493 0.367435 12.391 0.195266 12.2188C0.0230959 12.0466 -0.0352196 11.8439 0.020319 11.6106L0.686783 8.42829L3.98578 11.7273L0.803414 12.3937ZM3.98578 11.7273L0.686783 8.42829L8.31779 0.797279C8.57327 0.541801 8.88984 0.414062 9.2675 0.414062C9.64516 0.414062 9.96173 0.541801 10.2172 0.797279L11.6168 2.19685C11.8723 2.45233 12 2.7689 12 3.14656C12 3.52423 11.8723 3.8408 11.6168 4.09627L3.98578 11.7273ZM9.2675 1.73033L2.3696 8.62822L3.78584 10.0445L10.6837 3.14656L9.2675 1.73033Z" fill="#222222" />
                            </g>
                        </svg>
                    </div>
                </div>
                <div class="flex gap-2 relative items-baseline">
                <p class="sm">${boat.price}</p>
                <span class="js--inline-dialog-trigger w-6 h-6 p-1 bg-[rgb(224,223,220)] rounded-full justify-center items-center flex cursor-pointer leading-none">?</span>
                <dialog class="open:absolute open:inset-0 open:translate-y-3/4 open:md:translate-y-full open:md:translate-x-1/2 open:p-2 open:bg-[rgb(224,223,220)]">
                    <div class="text-pretty">
                      <span class="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center text-[var(--text-primary)] bg-[#E0DFDC] hover:bg-[#E9E8E6] hover:text-gray-900 active:bg-[#E9E8E6] active:text-gray-900 rounded-full size-6 text-2xl/none cursor-pointer">&times;</span>
                      ${boat?.pricingDetails}
                    </div>
                  </dialog>
                </div>
                <div class="gap-2 self-stretch inline-flex flex-col items-stretch md:flex-row md:items-center md:justify-start">
                    <a class="inline-flex justify-between gap-2 items-center chicklet is-link" href="${boat.designBoatLink}" target="_blank">
                        Design Boat
                        <svg class="size-4" width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.99536 1.25L8.99536 6.25L3.99536 11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </a>
                    <a class="inline-flex justify-between gap-2 items-center chicklet is-link" href="${boat.url}">
                        Learn More
                        <svg class="size-4" width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.99536 1.25L8.99536 6.25L3.99536 11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    `;
}

// Feature Extraction Transformation
function parseBoatFeatures(boat) {
  const features = {};

  boat.featureList.forEach((category) => {
    const headLine = category.headline.trim().toLowerCase();
    if (!features[headLine]) {
      features[headLine] = [];
    }

    category.features.forEach((feature) => {
      features[headLine].push({
        featureName: feature.featureName,
        featureDescription: feature.featureDescription,
        featureUpgrade: feature.featureUpgrade
      });
    });
  });

  return features;
}

// Core State Updates & Rendering
function updateUiSelections() {
  let activeBoxes = [];
  let activeBoats = [];
  let activeSeries = [];

  boatSelectElems.forEach((elem, i) => {
    const { boatId, boatSeries } = elem.dataset;
    const isSelected = elem.firstElementChild.ariaHidden === "true";

    if (isSelected) {
      activeBoxes.push(i);
    }
    if (boatId) {
      activeBoats.push(parseInt(boatId));
    }
    if (boatSeries && boatSeries.toLowerCase() === "prostar") {
      activeSeries.push(boatSeries);
    }
  });

  selectedBoxes = activeBoxes;
  selectedBoats = activeBoats;

  selectedSeries = activeSeries.filter((val, i, arr) => {
    return arr.indexOf(val) === i;
  });
}

function renderDropdownOptions() {
  // Refresh Family Options
  const filteredSeries = filterExcludedCollection(selectedSeries, allSeriesData, "series");
  boatFamilySelect.options.length = 0;

  filteredSeries.forEach((item, i) => {
    boatFamilySelect.options[i] = new Option(item.name, item.series);
  });

  // Refresh Model Options
  const localizedBoats = filterBoatsBySeriesName(boatFamilySelect.value, allBoatsData);
  const filteredBoats = filterExcludedCollection(selectedBoats, localizedBoats, "id");
  boatModelSelect.options.length = 0;

  filteredBoats.forEach((item, i) => {
    boatModelSelect.options[i] = new Option(item.name, item.id);
  });
}

function clearAccordionColumn(index) {
  if (!compareAccordion) return;

  const elementsToClear = compareAccordion.querySelectorAll(`.js--compare-value > [data-index="${index}"]`);
  elementsToClear.forEach((item) => {
    item.innerHTML = "-";
  });
}

function updateAccordionValues(features) {
  const descriptionKeys = ["design", "dimensions", "capacity"];

  for (const key in features) {
    const useDescription = descriptionKeys.includes(key);

    features[key].forEach((item) => {
      const fnId = cleanStringForId(item.featureName);
      const targetCell = document.querySelector(`#_${fnId} .js--compare-value > [data-index="${currSelection}"]`);

      if (targetCell) {
        targetCell.innerText = useDescription ? item.featureDescription : item.featureUpgrade;
      }
    });
  }
}

function runComparisonCheck() {
  const featureRows = document.querySelectorAll(".js--feature-wrapper");

  if (!compareCheckbox.checked || selectedBoxes.length < 2) {
    featureRows.forEach((row) => {
      row.ariaHidden = false;
    });
    return;
  }

  featureRows.forEach((row) => {
    const textValues = selectedBoxes.map((idx) => {
      const cell = row.querySelector(`[data-index="${idx}"]`);
      return cell ? cell.innerText : "";
    });

    const allMatch = textValues.every((val) => val === textValues[0]);
    row.ariaHidden = allMatch;
  });
}

// Action Handlers
export function showModal(e) {
  boatSelectModal.showModal();
  currSelection = e.currentTarget.dataset.index;
}

export function showToolTip(e) {
  e.preventDefault();
  const toolTip = e.currentTarget.parentElement.querySelector("dialog");
  toolTip.showModal();

  const closeBtn = toolTip.querySelector("span.absolute");
  toolTip.scroll({ top: 0 });

  const iframe = toolTip.querySelector("iframe");
  let originalSrc = "";

  if (iframe) {
    originalSrc = iframe.src;
    let autoplaySrc = originalSrc;
    if (!autoplaySrc.includes("autoplay=1")) {
      autoplaySrc += (autoplaySrc.includes("?") ? "&" : "?") + "autoplay=1";
    }
    iframe.src = autoplaySrc;
  }

  closeBtn.addEventListener("click", () => {
    toolTip.close();
    if (iframe) {
      iframe.setAttribute("src", "");
      iframe.setAttribute("src", originalSrc);
    }
  }, { once: true });
}

async function applySelectedBoatData(boatId, boatSeries) {
  const rawData = await getRequest(getBoatByIdUrl(boatId));
  const parsedFeatures = parseBoatFeatures(rawData);

  let container = document.getElementById(`boat-select-${currSelection}`);
  if (!container) return;

  container.dataset.boatId = boatId;
  container.dataset.boatSeries = boatSeries;
  container.removeEventListener("click", showModal);

  if (container.firstElementChild) {
    container.firstElementChild.ariaHidden = true;
  }
  if (container.lastElementChild && container.children.length > 1) {
    container.lastElementChild.remove();
  }

  const compiledHtml = generateBoatHtml(rawData);
  container.insertAdjacentHTML("beforeend", compiledHtml);

  const innerEditBtn = container.querySelector(".js--inline-modal-trigger");
  const innerPriceDescription = container.querySelector(".js--inline-dialog-trigger");

  if (innerEditBtn) {
    innerEditBtn.addEventListener("click", showModal);
  }
  if (innerPriceDescription) {
    innerPriceDescription.addEventListener("click", (e) => {
      e.preventDefault();
      const dialog = e.currentTarget?.parentElement?.querySelector('dialog');
      dialog?.show();
      const closeBtn = dialog?.querySelector("span.absolute");
      closeBtn?.addEventListener("click", () => {
        dialog?.close();
      }, { once: true });
    })
  }

  updateUiSelections();
  renderDropdownOptions();
  clearAccordionColumn(currSelection);
  updateAccordionValues(parsedFeatures);
  runComparisonCheck();
}

async function handleTabDefaultSelection(e) {
  e.preventDefault();
  const { boatid, boatseries } = e.currentTarget.dataset;

  currSelection = 0;
  await applySelectedBoatData(parseInt(boatid), boatseries);
}

async function handleFormSubmit(e) {
  e.preventDefault();
  if (boatModelSelect.options.length === 0) {
    alert("Select some other boat");
  }

  await applySelectedBoatData(boatModelSelect.value, boatFamilySelect.value);
  boatSelectModal.close();
}

function handleFamilyChange() {
  boatModelSelect.options.length = 0;
  const localizedBoats = filterBoatsBySeriesName(boatFamilySelect.value, allBoatsData);
  const filteredBoats = filterExcludedCollection(selectedBoats, localizedBoats, "id");

  filteredBoats.forEach((item, i) => {
    boatModelSelect.options[i] = new Option(item.name, item.id);
  });
}

function handleCompareCheckboxToggle() {
  runComparisonCheck();
}

function initializeStickyNav() {
  const skeleton = document.getElementById("compare-skeleton");
  if (skeleton) {
    skeleton.ariaHidden = false;
  }

  const stickyNavEl = document.querySelector("#sticky-nav-container");
  const masthead = document.querySelector("#masthead");

  ScrollTrigger.create({
    trigger: ".js--compare-boat",
    start: "30% 48px",
    end: () => `+=${skeleton ? skeleton.offsetHeight : 0}`,
    onEnter: (self) => {
      self.trigger.classList.add("stickyBox");
      if (stickyNavEl && masthead) {
        self.trigger.style.setProperty("--sticky-masthead-height", `${stickyNavEl.offsetHeight}px`);
        self.trigger.style.setProperty("--masthead-height-combined", `${stickyNavEl.offsetHeight + masthead.offsetHeight}px`);
      }
    },
    onLeave: (self) => self.trigger.classList.remove("stickyBox"),
    onEnterBack: (self) => self.trigger.classList.add("stickyBox"),
    onLeaveBack: (self) => self.trigger.classList.remove("stickyBox")
  });

  boatSelectElems.forEach((item) => item.classList.remove("aspect-square"));
}

// Initialization Orchestrator
async function initializeApp() {
  // API Fetch
  allBoatsData = await getRequest(ALL_BOATS_URL);
  allSeriesData = await getRequest(ALL_SERIES_URL);

  // Initial Binding
  boatSelectElems.forEach((item) => {
    item.addEventListener("click", showModal);
  });

  renderDropdownOptions();

  if (compTab) {
    compTab.addEventListener("click", handleTabDefaultSelection, { once: true });
    compTab.addEventListener("click", initializeStickyNav, { once: true });
  }

  submitBtn.addEventListener("click", handleFormSubmit);
  submitBtn.addEventListener("click", initializeStickyNav, { once: true });
  boatFamilySelect.addEventListener("change", handleFamilyChange);

  compareCheckbox.addEventListener("click", handleCompareCheckboxToggle);
}

if (boatSelectElems.length) {
  initializeApp();
}